import { createContext, useContext, ReactNode } from "react";
import { useRouter } from "next/router";

import { useCustomVariation } from "@/hooks/useCustomVariation";

/**
 * Hackle 실험 #338 - 개별 장지 견적 퍼널 롤백.
 *
 * 2026-08-10(PR #2497)에 한 번에 들어간 두 가지를 되돌린다.
 * - A(대조군, 현행): sticky 비용 게이지 노출 + 헤더 sub-nav 접힘
 * - B(실험군, 롤백): 게이지 미노출 + sub-nav 복원
 *
 * 게이지와 헤더를 쪼갠 2x2 는 하지 않는다. 4팔이면 6주 이상 걸려 그 사이 다른 변경이 들어간다.
 * A/B 에서 차이가 확인되면 그때 쪼갠다.
 *
 * 도입 전후 관측치(GA4 유저 단위, 07-23~08-09 vs 08-11~08-30)에서 응답률 -4.0%p,
 * 완주율 -3.4%p 였지만 같은 날 #319 승격과 진입 +17.5% 가 겹쳐 인과가 분리되지 않았다.
 * 이 실험이 그 분리를 맡는다. 배경은 wiki/features/platform-jangji-gauge-rollback-ab.md 참고.
 */
export const FACILITY_REQUEST_ROLLBACK_KEY = 338;

/**
 * 실험 모수. 화장장 진입(`/match/facility-request-crematorium`)은 같은 퍼널 컴포넌트를
 * 쓰지만 게이지도 sub-nav 접기도 걸린 적이 없어 모수가 아니다.
 */
const FACILITY_REQUEST_ROUTE = "/match/facility-requests/[id]";

const FacilityRequestRollbackContext = createContext(false);

/**
 * true 면 B(롤백). 모수 밖, 미할당("Z"), 로딩 중은 전부 false = 현행(A) 유지.
 *
 * 헤더와 견적 퍼널이 **같은 값을 봐야 한다.** 두 파일에서 각각 훅을 부르면 노출이 두 번
 * 기록되고 로딩 타이밍이 어긋나 헤더와 본문이 서로 다른 팔을 그리는 순간이 생긴다.
 */
export const useIsFacilityRequestRollback = () => useContext(FacilityRequestRollbackContext);

/**
 * 훅 호출 자체가 Hackle 노출 기록이다. 모수 라우트에서만 마운트해야 해서 컴포넌트로 뺐다.
 *
 * #296 은 이걸 안 해서 Header 에서 `useCustomVariation(296)` 을 전 페이지에서 호출했다.
 * 같은 실수를 반복하지 않기 위한 분리다.
 */
function AssignedProvider({ children }: { children: ReactNode }) {
  const variation = useCustomVariation(FACILITY_REQUEST_ROLLBACK_KEY);

  return (
    <FacilityRequestRollbackContext.Provider value={variation === "B"}>
      {children}
    </FacilityRequestRollbackContext.Provider>
  );
}

/**
 * Layout 최상단에 한 번만 둔다. 헤더와 퍼널이 모두 이 아래에 있어야 같은 값을 본다.
 *
 * 첫 페인트는 항상 A 다(Hackle 할당이 클라이언트에서 확정되기 때문). B 할당자는 할당 직후
 * sub-nav 가 펼쳐지며 한 번 밀린다. 안전한 방향(현행 -> 롤백)이라 그대로 둔다.
 */
export function FacilityRequestRollbackProvider({ children }: { children: ReactNode }) {
  const router = useRouter();

  if (router.pathname !== FACILITY_REQUEST_ROUTE) {
    return (
      <FacilityRequestRollbackContext.Provider value={false}>
        {children}
      </FacilityRequestRollbackContext.Provider>
    );
  }

  return <AssignedProvider>{children}</AssignedProvider>;
}
